import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"

import { cn } from "@/lib/utils"

const Drawer = ({
  shouldScaleBackground = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = "Drawer"

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn(
  // Elevate above sticky Navbar and popovers (Navbar uses z-[200], popovers ~ z-[120])
  // Use higher z to ensure the drawer overlays the Navbar on desktop
  "fixed inset-0 z-[300] bg-black/80",
      className
    )}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

type DrawerSide = "bottom" | "right"

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    side?: DrawerSide
  }
>(({ className, children, side = "bottom", ...props }, ref) => (
  <DrawerPortal>
  <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        // Posicionamiento y estilos según orientación
        side === "bottom"
          ? [
              // Overlay content above Navbar as well
              "fixed inset-x-0 bottom-0 z-[300] flex flex-col",
              // Height is controlled by the caller (e.g., VisitBookingModal) via inline min/max using dvh/vh
              "rounded-t-[10px] border bg-background overflow-hidden touch-manipulation",
              "[--tw-bg-opacity:1]",
              "-webkit-overflow-scrolling-touch",
            ].join(" ")
          : [
              // Right-side desktop drawer above Navbar
              "fixed inset-y-0 right-0 z-[300] flex h-full w-[92vw] max-w-[560px] flex-col",
              "rounded-l-[10px] border-l bg-background overflow-hidden",
            ].join(" "),
        className
      )}
      style={{
        WebkitOverflowScrolling: 'touch',
    paddingBottom: side === "bottom" ? 'calc(env(safe-area-inset-bottom, 0px))' : undefined,
        ...props.style,
      }}
      {...props}
    >
      {side === "bottom" && (
        <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
      )}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
))
DrawerContent.displayName = "DrawerContent"

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
